import React, { useState, useRef, useEffect } from "react";
import { Card as RawCard } from "@/components/ui/card";
import { Button as RawButton } from "@/components/ui/button";
import { Input as RawInput } from "@/components/ui/input";
import { Label as RawLable } from "@/components/ui/lable";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  User,
  Mail,
  Award,
  Loader2,
  Camera,
  Phone,
  MapPin,
} from "lucide-react";
import { motion } from "framer-motion";
import StatusDialog from "../ui/statusloading";

import {
  uploadProfileImage,
  deleteProfileImage,
} from "../../services/profileService";
import { DIAL_CODES, DEFAULT_DIAL_CODE } from "@/data/countries";
import {
  isValidMobileNumber,
  isValidPostcode,
  GOLF_HANDICAP_OPTIONS,
} from "@/utils/validators";

const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

// Detects the real image type from the file's first bytes, ignoring the
// extension and the browser-reported file.type (both are user-controlled).
async function detectImageType(file) {
  const bytes = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  const at = (sig, offset = 0) => sig.every((b, i) => bytes[offset + i] === b);

  if (at([0xff, 0xd8, 0xff])) return "image/jpeg";
  if (at([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return "image/png";
  if (at([0x52, 0x49, 0x46, 0x46]) && at([0x57, 0x45, 0x42, 0x50], 8)) {
    return "image/webp"; // "RIFF" .... "WEBP"
  }
  return null;
}

async function validatePhotoFile(file) {
  if (file.size > MAX_PHOTO_BYTES) {
    return { error: "Photo must be smaller than 5MB." };
  }

  const type = await detectImageType(file);
  if (!type) {
    return { error: "Please choose a JPG, PNG or WebP image." };
  }

  // Correct header but truncated/corrupt data would still render broken,
  // so confirm the browser can actually decode it.
  try {
    const bitmap = await createImageBitmap(file);
    bitmap.close();
  } catch {
    return {
      error: "This image appears to be corrupted. Please choose another file.",
    };
  }

  return { type };
}

/**
 * @typedef {Object} UserProfile
 * @property {string} [fullName]
 * @property {number} [golf_handicap]
 * @property {string} [profile_photo_url]
 * @property {string} [email]
 * @property {string} [mobile_country_code]
 * @property {string} [mobile_number]
 * @property {string} [postcode]
 *
 * @typedef {Object} PersonalInfoSectionProps
 * @property {UserProfile} [user]
 * @property {string} [uid]
 * @property {(data: Record<string, any>) => Promise<any>} onUpdate
 */

export default function PersonalInfoSection(
  /** @type {PersonalInfoSectionProps} */ { user, uid, onUpdate },
) {
  const [fullName, setFullName] = useState(user?.fullName || "");

  const [golfHandicap, setGolfHandicap] = useState(
    user?.golf_handicap !== undefined && user?.golf_handicap !== null
      ? String(user.golf_handicap)
      : "",
  );

  const [mobileCountryCode, setMobileCountryCode] = useState(
    user?.mobile_country_code || DEFAULT_DIAL_CODE,
  );
  const [mobileNumber, setMobileNumber] = useState(user?.mobile_number || "");
  const [postcode, setPostcode] = useState(user?.postcode || "");

  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(
    user?.profile_photo_url || null,
  );
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);

  const [dialog, setDialog] = useState({
    open: false,
    variant: /** @type {"success" | "error"} */ ("success"),
    title: "",
    description: "",
  });

  // Re-sync when user data arrives/changes after this component has
  // already mounted (e.g. async Firestore fetch resolving post-render).
  useEffect(() => {
    setFullName(user?.fullName || "");
    setGolfHandicap(
      user?.golf_handicap !== undefined && user?.golf_handicap !== null
        ? String(user.golf_handicap)
        : "",
    );
    setMobileCountryCode(user?.mobile_country_code || DEFAULT_DIAL_CODE);
    setMobileNumber(user?.mobile_number || "");
    setPostcode(user?.postcode || "");
    setPhotoPreview(user?.profile_photo_url || null);
    // console.log(user?.profile_photo_url);
  }, [user]);

  const Label = /** @type {React.ComponentType<any>} */ (RawLable);
  const Button = /** @type {React.ComponentType<any>} */ (RawButton);
  const Card = /** @type {React.ComponentType<any>} */ (RawCard);
  const Input = /** @type {React.ComponentType<any>} */ (RawInput);

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    // Reset so picking the same file again after a rejection still fires onChange.
    e.target.value = "";
    if (!file) return;

    if (!uid) {
      console.error(
        "Cannot upload photo: no uid provided to PersonalInfoSection.",
      );
      alert(
        "Can't upload photo right now — user not identified. Try refreshing.",
      );
      return;
    }

    const { error, type } = await validatePhotoFile(file);
    if (error) {
      setDialog({
        open: true,
        variant: "error",
        title: "Invalid photo",
        description: error,
      });
      return;
    }

    // Re-wrap with the detected type so the contentType stored in Firebase
    // Storage matches the real content, not whatever the extension claimed.
    const safeFile = new File([file], file.name, { type });

    setIsUploadingPhoto(true);
    const previewUrl = URL.createObjectURL(safeFile);
    setPhotoPreview(previewUrl);

    try {
      const oldUrl = user?.profile_photo_url;
      const newUrl = await uploadProfileImage(uid, safeFile);
      setPhotoPreview(newUrl);

      if (oldUrl && oldUrl !== newUrl) {
        // Separate try/catch: if cleanup of the old file fails, the new
        // upload still succeeded and must not be reported as a failure.
        try {
          await deleteProfileImage(uid, oldUrl);
        } catch (delErr) {
          console.warn("Could not delete previous profile photo:", delErr);
        }
      }
    } catch (err) {
      console.error("Photo upload failed:", err);
      setDialog({
        open: true,
        variant: "error",
        title: "Failed to upload photo",
        description: "Failed to upload photo. Please try again.",
      });
      setPhotoPreview(user?.profile_photo_url || null);
    } finally {
      setIsUploadingPhoto(false);
      URL.revokeObjectURL(previewUrl);
    }
  };

  const validate = () => {
    const nextErrors = {};

    if (!mobileNumber.trim()) {
      nextErrors.mobileNumber = "Please fill in your mobile number";
    } else if (!isValidMobileNumber(mobileNumber)) {
      nextErrors.mobileNumber = "Mobile number must contain numbers only";
    }

    if (!isValidPostcode(postcode)) {
      nextErrors.postcode = "Postcode must contain numbers only";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setIsSaving(true);
    try {
      await onUpdate({
        fullName: fullName,
        golf_handicap: golfHandicap ? parseFloat(golfHandicap) : null,
        mobile_country_code: mobileCountryCode,
        mobile_number: mobileNumber,
        postcode: postcode,
      });

      setDialog({
        open: true,
        variant: "success",
        title: "Personal details saved",
        description: "Personal information updated successfully!",
      });
    } catch (error) {
      console.error("Update error:", error);
      setDialog({
        open: true,
        variant: "error",
        title: "Failed to update information",
        description: "Failed to update information. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card className="p-6 bg-white border border-gray-100">
          <h2 className="text-2xl font-bold mb-6 text-[#0B2421]">
            Personal Information
          </h2>

          <div className="flex flex-col items-center mb-8">
            <div className="relative">
              <div
                onClick={() =>
                  !isUploadingPhoto && fileInputRef.current?.click()
                }
                className={`w-24 h-24 rounded-full bg-gray-200 overflow-hidden border-4 border-white shadow-md ${
                  isUploadingPhoto
                    ? "cursor-not-allowed"
                    : "cursor-pointer hover:opacity-90"
                }`}
              >
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt="Profile"
                    className="w-full h-full object-cover"
                    // onError={(e) => {
                    //   console.error("Image failed to load:", photoPreview, e);
                    // }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="h-10 w-10 text-gray-400" />
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingPhoto}
                className="absolute bottom-0 right-0 h-8 w-8 rounded-full bg-emerald-600 text-white flex items-center justify-center shadow-md hover:bg-emerald-700 transition-colors"
              >
                {isUploadingPhoto ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Camera className="h-4 w-4" />
                )}
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="hidden"
              />
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Click the camera icon to upload a photo
            </p>
          </div>

          <div className="space-y-5">
            <div>
              <Label
                htmlFor="fullName"
                className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2"
              >
                <User className="h-4 w-4" />
                Full Name
              </Label>
              <Input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Enter your full name"
                className="h-12 text-gray-700"
              />
            </div>

            <div>
              <Label
                htmlFor="email"
                className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2"
              >
                <Mail className="h-4 w-4" />
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={user?.email || ""}
                disabled
                placeholder="Enter your email address"
                className="h-12 text-gray-700 bg-transparent disabled:opacity-100"
              />
              <p className="text-xs text-gray-500 mt-1">
                Email cannot be changed
              </p>
            </div>

            <div>
              <Label
                htmlFor="mobileNumber"
                className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2"
              >
                <Phone className="h-4 w-4" />
                Mobile Number *
              </Label>
              <div className="flex gap-2">
                <Select
                  value={mobileCountryCode}
                  onValueChange={setMobileCountryCode}
                >
                  <SelectTrigger className="h-12 text-gray-700 w-28 shrink-0">
                    <SelectValue placeholder="+65" />
                  </SelectTrigger>
                  <SelectContent className="bg-white text-black max-h-72">
                    {DIAL_CODES.map((d) => (
                      <SelectItem key={`${d.country}-${d.dial}`} value={d.dial}>
                        {d.dial} {d.country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  id="mobileNumber"
                  type="tel"
                  inputMode="numeric"
                  value={mobileNumber}
                  onChange={(e) =>
                    setMobileNumber(e.target.value.replace(/[^0-9]/g, ""))
                  }
                  placeholder="Enter your mobile number"
                  className="h-12 text-gray-700 flex-1"
                />
              </div>
              {errors.mobileNumber && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.mobileNumber}
                </p>
              )}
            </div>

            <div>
              <Label
                htmlFor="postcode"
                className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2"
              >
                <MapPin className="h-4 w-4" />
                Postcode
              </Label>
              <Input
                id="postcode"
                type="text"
                inputMode="numeric"
                value={postcode}
                onChange={(e) =>
                  setPostcode(e.target.value.replace(/[^0-9]/g, ""))
                }
                placeholder="Enter your postcode"
                className="h-12 text-gray-700"
              />
              <p className="text-xs text-gray-500 mt-1">Optional</p>
              {errors.postcode && (
                <p className="text-xs text-red-500 mt-1">{errors.postcode}</p>
              )}
            </div>

            <div>
              <Label
                htmlFor="handicap"
                className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2"
              >
                <Award className="h-4 w-4" />
                Golf Handicap
              </Label>
              <Select value={golfHandicap} onValueChange={setGolfHandicap}>
                <SelectTrigger className="h-12 text-gray-700">
                  <SelectValue placeholder="Select your golf handicap" />
                </SelectTrigger>
                <SelectContent className="bg-white text-black max-h-72">
                  {GOLF_HANDICAP_OPTIONS.map((h) => (
                    <SelectItem key={h} value={h}>
                      {h}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Optional: Your official golf handicap index (0-50)
              </p>
            </div>

            <div className="pt-4">
              <Button
                onClick={handleSave}
                disabled={isSaving}
                className="w-full sm:w-auto hover:bg-[#0B2421]/85 h-12 text-white bg-[#0B2421]"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </div>
          </div>
        </Card>
      </motion.div>
      <StatusDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        variant={dialog.variant}
        title={dialog.title}
        description={dialog.description}
      />
    </>
  );
}
